import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

import { 
  Gift, 
  Crown, 
  Star, 
  Trophy,
  Sparkles,
  Shield,
  Zap,
  MessageSquare,
  Palette,
  Lock,
  Check,
  ChevronRight,
  Award,
  FileText
} from 'lucide-react';


const TIER_ICONS = {
  supporter: { icon: Star, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  super_fan: { icon: Sparkles, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  vip_fan: { icon: Crown, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  legend: { icon: Trophy, color: 'text-red-400', bg: 'bg-red-500/10' }
};

function TierCard({ tier, isCurrentTier, isUnlocked }) {
  const tierStyle = TIER_ICONS[tier.tier_id] || { icon: Star, color: 'text-gray-400', bg: 'bg-gray-500/10' };
  const Icon = tierStyle.icon;

  return (
    <Card 
      className={`p-6 relative overflow-hidden transition-all ${
        isCurrentTier 
          ? 'border-2 border-primary ring-2 ring-primary/20' 
          : isUnlocked 
            ? 'border border-border' 
            : 'border border-border opacity-70'
      }`}
      data-testid={`tier-card-${tier.tier_id}`}
    >
      {isCurrentTier && (
        <Badge className="absolute top-2 right-2 bg-primary">Current Tier</Badge>
      )}
      
      <div className="flex items-center gap-4 mb-4">
        <div className={`w-14 h-14 rounded-xl ${tierStyle.bg} flex items-center justify-center`}>
          <Icon className={`w-7 h-7 ${tierStyle.color}`} />
        </div>
        <div>
          <h3 className="font-bold text-lg">{tier.name}</h3>
          <p className="text-sm text-muted-foreground">
            {tier.min_coins.toLocaleString()} coins to unlock
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <span>{tier.benefits?.chat?.max_message_length || 200} char messages</span>
        </div>
        {tier.benefits?.chat?.username_color && (
          <div className="flex items-center gap-2 text-sm">
            <Palette className="w-4 h-4 text-muted-foreground" />
            <span>Colored username</span>
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: tier.benefits.chat.username_color }}
            />
          </div>
        )}
        {tier.benefits?.chat?.custom_emojis && (
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-muted-foreground" />
            <span>Custom emojis</span>
          </div>
        )}
        {tier.benefits?.flair_count > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Crown className="w-4 h-4 text-muted-foreground" />
            <span>{tier.benefits.flair_count} profile flair options</span>
          </div>
        )}
        {tier.benefits?.early_access_count > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Zap className="w-4 h-4 text-muted-foreground" />
            <span>{tier.benefits.early_access_count} early access features</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function BadgeDisplay({ badge, earned }) {
  return (
    <div 
      className={`p-4 rounded-lg text-center transition-all ${
        earned ? 'bg-card/80' : 'bg-card/30 opacity-50'
      }`}
      data-testid={`badge-${badge.badge_id}`}
    >
      <div className="text-4xl mb-2">{badge.icon}</div>
      <p className="font-medium text-sm">{badge.name}</p>
      <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
      {earned && (
        <Badge variant="outline" className="mt-2 text-xs bg-green-500/10 text-green-400 border-green-500/30">
          <Check className="w-3 h-3 mr-1" /> Earned
        </Badge>
      )}
      {!earned && (
        <Badge variant="outline" className="mt-2 text-xs">
          <Lock className="w-3 h-3 mr-1" /> Locked
        </Badge>
      )}
    </div>
  );
}

export default function PerksPage() {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [perksInfo, setPerksInfo] = useState(null);
  const [myProgress, setMyProgress] = useState(null);
  const [badges, setBadges] = useState({ all: [], earned: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerksInfo();
    if (isAuthenticated) {
      fetchMyProgress();
      fetchMyBadges();
    }
  }, [isAuthenticated]);

  const fetchPerksInfo = async () => {
    try {
      const response = await fetch(`${API}/perks/info`);
      if (response.ok) {
        setPerksInfo(await response.json());
      }
    } catch (error) {
      console.error('Error fetching perks info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProgress = async () => {
    try {
      const response = await fetch(`${API}/perks/my-progress`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMyProgress(await response.json());
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const fetchMyBadges = async () => {
    try {
      const [allResponse, myResponse] = await Promise.all([
        fetch(`${API}/rewards/badges`),
        fetch(`${API}/rewards/my-badges`, { headers: getAuthHeaders() })
      ]);
      
      if (allResponse.ok) {
        const allData = await allResponse.json();
        setBadges(prev => ({ ...prev, all: allData.badges || [] }));
      }
      if (myResponse.ok) {
        const myData = await myResponse.json();
        setBadges(prev => ({ ...prev, earned: myData.earned || [] }));
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const currentTierId = myProgress?.tier?.current?.tier_id || 'none';
  const earnedBadgeIds = badges.earned.map(b => b.badge_id);

  return (
    <div className="min-h-screen py-8 px-4" data-testid="perks-page">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/20 via-background to-secondary/20 rounded-2xl py-16 px-8 mb-8 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Gift className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Listener Perks Program</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Support Artists, <span className="text-secondary">Earn Rewards</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            The more you support DMV artists, the more perks you unlock. 
            Climb the tiers, earn badges, and get exclusive benefits.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-8">
        {/* My Progress (Authenticated Users) */}
        {isAuthenticated && myProgress && (
          <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Your Progress
            </h2>
            <div className="grid gap-6 md:grid-cols-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Tier</p>
                <p className="font-bold text-lg">
                  {myProgress.tier?.current?.name || 'New Listener'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                <p className="font-bold text-lg text-secondary">
                  {myProgress.tier?.total_spent?.toLocaleString() || 0} coins
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Badges Earned</p>
                <p className="font-bold text-lg">
                  {myProgress.badges?.earned || 0} / {myProgress.badges?.total || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Gifts Sent</p>
                <p className="font-bold text-lg">{myProgress.stats?.gifts_sent || 0}</p>
              </div>
            </div>
            
            {myProgress.tier?.next && (
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>Progress to {myProgress.tier.next.name}</span>
                  <span>{myProgress.tier.progress_percent}%</span>
                </div>
                <Progress value={myProgress.tier.progress_percent} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  {myProgress.tier.coins_to_next?.toLocaleString()} more coins needed
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="tiers" className="w-full">
          <TabsList className="w-full grid grid-cols-3 bg-card/50 border border-border p-1">
            <TabsTrigger value="tiers" className="flex items-center gap-2">
              <Crown className="w-4 h-4" />
              <span className="hidden sm:inline">Tiers</span>
            </TabsTrigger>
            <TabsTrigger value="badges" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Badges</span>
            </TabsTrigger>
            <TabsTrigger value="terms" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Terms</span>
            </TabsTrigger>
          </TabsList>

          {/* Tiers Tab */}
          <TabsContent value="tiers" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {perksInfo?.tiers?.map((tier) => {
                const tierOrder = ['supporter', 'super_fan', 'vip_fan', 'legend'];
                const currentIndex = tierOrder.indexOf(currentTierId);
                const tierIndex = tierOrder.indexOf(tier.tier_id);
                const isUnlocked = tierIndex <= currentIndex;
                
                return (
                  <TierCard 
                    key={tier.tier_id}
                    tier={tier}
                    isCurrentTier={tier.tier_id === currentTierId}
                    isUnlocked={isUnlocked}
                  />
                );
              })}
            </div>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="mt-6">
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {badges.all.map((badge) => (
                <BadgeDisplay 
                  key={badge.badge_id}
                  badge={badge}
                  earned={earnedBadgeIds.includes(badge.badge_id)}
                />
              ))}
            </div>
          </TabsContent>

          {/* Terms Tab */}
          <TabsContent value="terms" className="mt-6">
            <Card className="p-6 bg-card/50 border border-border">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">Program Terms</h2>
              </div>
              
              {/* Key Points */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-amber-400 mb-2">Important Information</h3>
                <ul className="space-y-2 text-sm">
                  {perksInfo?.legal?.summary?.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Full Terms Link */}
              <Link to="/perks/terms">
                <Button variant="outline" className="w-full">
                  <FileText className="w-4 h-4 mr-2" />
                  View Full Terms & Conditions
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA for non-authenticated users */}
        {!isAuthenticated && (
          <Card className="p-8 text-center bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
            <Gift className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="text-xl font-bold mb-2">Start Earning Rewards</h3>
            <p className="text-muted-foreground mb-4">
              Sign in to track your progress and unlock exclusive perks
            </p>
            <Link to="/login">
              <Button size="lg">
                Sign In to Get Started
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        )}
      </div>
    </div>
  );
}
